/**
 * Page de gestion des comptes ANEF rattachés
 * Permet de suivre les dossiers d'autres personnes
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Chip,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Sync,
  Edit,
  Delete,
  Warning,
  CheckCircle,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { linkedAnefAccountsApi } from '../../services/clientApi';

interface LinkedAccount {
  id: number;
  linked_anef_identifiant: string;
  display_name: string | null;
  relation: string | null;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: 'success' | 'auth_failed' | 'error';
  needs_password_update: boolean;
  password_error_count: number;
  dossiers_count: number;
  created_at: string;
}

const RELATIONS = [
  { value: 'conjoint', label: 'Conjoint(e)' },
  { value: 'enfant', label: 'Enfant' },
  { value: 'parent', label: 'Parent' },
  { value: 'frere_soeur', label: 'Frère/Sœur' },
  { value: 'ami', label: 'Ami(e)' },
  { value: 'autre', label: 'Autre' },
];

export default function LinkedAnefAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<LinkedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<number | null>(null);

  // Dialog d'ajout/modification
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<LinkedAccount | null>(null);
  const [formData, setFormData] = useState({
    anef_identifiant: '',
    anef_password: '',
    display_name: '',
    relation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await linkedAnefAccountsApi.getAll();
      setAccounts(response.data.linked_accounts);
    } catch (error: any) {
      console.error('Erreur chargement comptes:', error);
      if (error.response?.status === 403) {
        setError('Cette fonctionnalité est réservée aux clients auto-inscrits');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (account?: LinkedAccount) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        anef_identifiant: account.linked_anef_identifiant,
        anef_password: '',
        display_name: account.display_name || '',
        relation: account.relation || '',
      });
    } else {
      setEditingAccount(null);
      setFormData({
        anef_identifiant: '',
        anef_password: '',
        display_name: '',
        relation: '',
      });
    }
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAccount(null);
    setFormData({
      anef_identifiant: '',
      anef_password: '',
      display_name: '',
      relation: '',
    });
    setError('');
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!formData.anef_identifiant || !formData.anef_password) {
      setError('Identifiant et mot de passe ANEF requis');
      return;
    }

    try {
      if (editingAccount) {
        await linkedAnefAccountsApi.update(editingAccount.id, {
          anef_password: formData.anef_password,
          display_name: formData.display_name || undefined,
          relation: formData.relation || undefined,
        });
        setSuccess('Compte mis à jour avec succès');
      } else {
        await linkedAnefAccountsApi.create({
          anef_identifiant: formData.anef_identifiant,
          anef_password: formData.anef_password,
          display_name: formData.display_name || undefined,
          relation: formData.relation || undefined,
        });
        setSuccess('Compte ajouté avec succès');
      }
      handleCloseDialog();
      fetchAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleSync = async (accountId: number) => {
    try {
      setSyncing(accountId);
      await linkedAnefAccountsApi.sync(accountId);
      setSuccess('Synchronisation réussie');
      fetchAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      if (error.response?.data?.needs_password_update) {
        setError('Mot de passe ANEF invalide. Veuillez le mettre à jour.');
      } else {
        setError(error.response?.data?.message || 'Erreur de synchronisation');
      }
      setTimeout(() => setError(''), 5000);
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async (accountId: number, displayName: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir détacher le compte "${displayName}" ?`)) {
      return;
    }

    try {
      await linkedAnefAccountsApi.delete(accountId);
      setSuccess('Compte détaché avec succès');
      fetchAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erreur lors de la suppression');
      setTimeout(() => setError(''), 5000);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LinearProgress sx={{ width: '100%', maxWidth: 300 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ position: 'sticky', top: 0, zIndex: 10, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
          <IconButton onClick={() => navigate('/client/settings')} size="small">
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
              Comptes ANEF rattachés
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Suivez les dossiers d'autres personnes
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Ajouter
          </Button>
        </Box>
      </Paper>

      <Container maxWidth="md" sx={{ mt: 3, px: 2 }}>
        {/* Messages */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            À quoi ça sert ?
          </Typography>
          <Typography variant="caption">
            Vous pouvez suivre les dossiers ANEF d'autres personnes (famille, amis) en ajoutant leurs identifiants.
            Les dossiers seront synchronisés et affichés dans votre dashboard.
          </Typography>
        </Alert>

        {/* Liste des comptes */}
        {accounts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Aucun compte rattaché
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2 }}
            >
              Ajouter un compte
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {accounts.map((account) => (
              <Card key={account.id} variant="outlined">
                <CardContent sx={{ p: 2 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {account.display_name || 'Compte sans nom'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                        {account.linked_anef_identifiant}
                      </Typography>
                    </Box>
                    {account.relation && (
                      <Chip
                        label={RELATIONS.find(r => r.value === account.relation)?.label || account.relation}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Infos */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${account.dossiers_count} dossier${account.dossiers_count > 1 ? 's' : ''}`}
                      size="small"
                      icon={<CheckCircle />}
                    />

                    {account.needs_password_update ? (
                      <Chip
                        label="Mot de passe invalide"
                        size="small"
                        color="error"
                        icon={<Warning />}
                      />
                    ) : account.last_sync_status === 'success' ? (
                      <Chip
                        label={account.last_sync_at ? `Sync: ${new Date(account.last_sync_at).toLocaleDateString('fr-FR')}` : 'Jamais synchronisé'}
                        size="small"
                        color="success"
                      />
                    ) : account.last_sync_status === 'error' ? (
                      <Chip
                        label="Erreur de sync"
                        size="small"
                        color="warning"
                      />
                    ) : null}
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={syncing === account.id ? null : <Sync />}
                      onClick={() => handleSync(account.id)}
                      disabled={syncing === account.id}
                    >
                      {syncing === account.id ? 'Sync...' : 'Synchroniser'}
                    </Button>

                    {account.needs_password_update && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<Edit />}
                        onClick={() => handleOpenDialog(account)}
                      >
                        Mettre à jour
                      </Button>
                    )}

                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(account)}
                      sx={{ ml: 'auto' }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(account.id, account.display_name || account.linked_anef_identifiant)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>

      {/* Dialog d'ajout/modification */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAccount ? 'Modifier le compte' : 'Ajouter un compte ANEF'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Identifiant ANEF *"
              value={formData.anef_identifiant}
              onChange={(e) => setFormData({ ...formData, anef_identifiant: e.target.value })}
              disabled={!!editingAccount}
              placeholder="Ex: 9765061055"
              fullWidth
            />

            <TextField
              label="Mot de passe ANEF *"
              type={showPassword ? 'text' : 'password'}
              value={formData.anef_password}
              onChange={(e) => setFormData({ ...formData, anef_password: e.target.value })}
              placeholder="Mot de passe du compte ANEF"
              fullWidth
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            <TextField
              label="Nom d'affichage"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              placeholder="Ex: Dossier de Marie"
              fullWidth
            />

            <TextField
              select
              label="Relation"
              value={formData.relation}
              onChange={(e) => setFormData({ ...formData, relation: e.target.value })}
              fullWidth
            >
              <MenuItem value="">Aucune</MenuItem>
              {RELATIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.anef_identifiant || !formData.anef_password}
          >
            {editingAccount ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
